<script>
/**
 * Card de prévia do painel de artefato — redesign visual dos 3 estados
 * (default/hover/ativo), a partir dos mockups do usuário (plano
 * `moonlit-conjuring-sun`). Substitui a renderização inline de um card de
 * dado do `UI_REGISTRY` por um card compacto e clicável.
 *
 * ★ O estado "visto"/"Reabrir artefato" foi REMOVIDO por pedido explícito do
 * usuário após teste ao vivo: o CTA (quando não-ativo) mostra sempre "Abrir
 * artefato", nunca importa se o card já foi aberto antes.
 *
 * ★ O tile do ícone é um quadrado arredondado (40×40, raio `--radius-md`),
 * CENTRADO verticalmente no card, à esquerda — o padrão AAWZ. O tile
 * "afundado" no rodapé, estilo Claude.ai, foi tentado em seis rodadas e
 * DESCARTADO pelo usuário em 11/09/2026. O modo `mini` é a MESMA regra, só
 * menor (32×32). É desenhado aqui mesmo, não pelo `IconWithBackground`
 * (que não sabe ser cortado nem descentralizar o ícone). E o ícone dentro
 * dele anima no hover do card com
 * a MESMA animação, POR ÍCONE, do menu lateral de módulos: o partial
 * `navegacao-v2/styles/_nav-icon-tells.sass` (o mapa `is-animating.icon-<nome>`
 * que o `nav-item.vue` também importa) + as classes `navbar-main-icon` /
 * `is-detailed` / `is-animating`, com o hover rastreado por JS exatamente
 * como lá — o `:hover` do CSS não alterna classe em OUTRO elemento.
 *
 * ★ Modo `mini` (prop): a LINHA do índice "Artefatos desta conversa"
 * (`chat-indice-artefatos.vue`). Mesmo card, sem CTA/chip — tile 32×32
 * normal (não afundado), largura livre (100% da linha), padding compacto,
 * `tempo` discreto + chevron à direita, `aria-current` quando ativo. Título,
 * subtítulo, animação do ícone e estado ativo (tile sólido + ícone branco)
 * continuam os mesmos.
 *
 * ★ Leitura de store SEM o helper `mapGetters` de propósito. `mapGetters`
 * com namespace string chama `getModuleByNamespace`, que lê
 * `store._modulesNamespaceMap` — inexistente quando este componente é
 * montado (via `PartesDoTurno`/`chat-message.vue`) com um `$store` mockado
 * como objeto plano (`{ getters: {...}, dispatch: jest.fn() }`), padrão já
 * estabelecido em `partes-do-turno.unit.js`. A mesma leitura direta
 * (`store.getters['<ns>/<getter>']`) já é usada por
 * `renderiza-partes.js::modoArtefatoAtivo` pelo mesmo motivo.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
 */
import { DETAILED_ANIMATION_ICONS } from '@modules/global/components/button-navbar/button-navbar.constants'
import { ARTEFATO_COPY } from '@modules/aawz-intelligence-v2/constants/TextChat'

const STORE_MODULE = 'aawz-intelligence-v2'
// Ícone dentro do tile de 40px — entre os 16 da nav antiga e os 19 do
// `nav-item.vue`, proporcional ao tile.
const TAMANHO_DO_ICONE = 18
// No tile de 32px do modo `mini`, o mesmo passo de proporção.
const TAMANHO_DO_ICONE_MINI = 16
const TAMANHO_DO_CHEVRON = 16
// Cor do ícone por estado: `primary` sobre o tint translúcido, branco sobre
// o tile sólido do ativo. Nomes de token — o `icon-svg` embrulha em `var(--x)`.
const COR_DO_ICONE = { padrao: 'primary', ativo: 'white' }

export default {
  name: 'ArtefatoPreviewCard',
  props: {
    // Identidade única da parte (`parte.toolCallId || chave`, a MESMA usada
    // pelo `:key` do renderer) — é o que este card compara contra
    // `artefatoIdentidadeAtiva` para saber se É o artefato aberto agora.
    identidade: { type: String, required: true },
    // Título do card (ex.: `entrada.label` do UI_REGISTRY — "Oportunidades").
    titulo: { type: String, required: true },
    // Nome de `icon-svg`, resultado de `resolverIconeDoModulo`. Default é o
    // mesmo fallback genérico do resolver, para o card nunca ficar sem ícone.
    icone: { type: String, default: 'grid-six' },
    // Tipo de visualização (ex.: "Tabela"). Não é mais exibido no card — a
    // linha "Artefato" foi removida por completo (pedido do usuário; o CTA
    // agora diz só "Abrir artefato"). Mantido só por compatibilidade, para
    // não quebrar quem já passa a prop.
    tipo: { type: String, default: '' },
    // Resumo curto e opcional (ex.: nome do cliente, ou "1.667 linhas · CRM").
    // Vazio por padrão — o card só mostra o título, como sempre mostrou.
    subtitulo: { type: String, default: '' },
    // Linha do índice de artefatos (ver o cabeçalho). Default = o card do
    // thread, como sempre foi.
    mini: { type: Boolean, default: false },
    // Só no `mini`: "agora" / "há 4 min", já formatado por quem monta a
    // lista. Vazio = a coluna de tempo não aparece (a mensagem viva não tem
    // `created_at`, e o card não inventa um).
    tempo: { type: String, default: '' },
    /**
     * O namespace Vuex de QUEM montou o card.
     *
     * ⚠️ [owner, 20/09/2026] *"o artefato dentro do chat não está ficando com
     * estilo de selecionado quando SELECIONADO"*. Ele era fixo
     * (`aawz-intelligence-v2`) enquanto TODO mundo que abre o aside despacha
     * em `this.storeModule` — que na Home IA é a CONVERSA
     * (`iaConversa:<uuid>`). Escrevia num módulo e lia no outro: o card nunca
     * se via ativo, e sem erro nenhum. Mesma família de corte do plano e do
     * `modoArtefatoAtivo` (§5.1 do CLAUDE.md do módulo).
     *
     * O default é o módulo fixo — as superfícies que não passam a prop
     * (popup, painel do Hub, Fee-Based) seguem byte a byte como antes.
     */
    storeModule: { type: String, default: STORE_MODULE },
  },
  data() {
    return {
      copy: ARTEFATO_COPY,
      TAMANHO_DO_CHEVRON,
      // Hover rastreado via JS (não só `:hover` do CSS) porque a classe
      // `is-animating` precisa entrar no `<icon-svg>`, e o `:hover` do CSS
      // não consegue alternar classe em outro elemento — mesmo motivo do
      // `nav-item.vue`.
      isHover: false,
    }
  },
  computed: {
    // Estado 3 do mock: este É o artefato aberto no aside agora.
    ativo() {
      return this.artefatoIdentidadeAtiva() === this.identidade
    },
    // Ícone com animação "detalhada" (parte a parte) — a mesma lista que a
    // navbar usa; vira a classe `is-detailed` no `<icon-svg>`.
    isDetailedIcon() {
      return DETAILED_ANIMATION_ICONS.includes(this.icone)
    },
    corDoIcone() {
      return this.ativo ? COR_DO_ICONE.ativo : COR_DO_ICONE.padrao
    },
    tamanhoDoIcone() {
      return this.mini ? TAMANHO_DO_ICONE_MINI : TAMANHO_DO_ICONE
    },
    // Utilities do Vuetify são `!important`, então o padding do card muda por
    // CLASSE, não por CSS: zero no thread (o tile precisa ser cortado pela
    // borda), compacto na linha do índice.
    classesDoCard() {
      return {
        'artefato-preview-card--ativo': this.ativo,
        'artefato-preview-card--mini': this.mini,
        'pa-0': !this.mini,
        'px-3 py-2': this.mini,
      }
    },
    // Os dois modos centram o tile; o respiro à esquerda no thread é `ml-3`
    // (no mini quem dá o respiro é o `px-3` da própria linha).
    classesDoTile() {
      return { 'align-self-center': true, 'ml-3': !this.mini }
    },
    classesDoTexto() {
      return this.mini ? 'py-0 pl-3' : 'py-3 pl-4'
    },
    ariaCurrent() {
      return this.mini && this.ativo ? 'true' : null
    },
  },
  methods: {
    // Clicar no card JÁ ativo fecha o painel (toggle) em vez de reabrir o que
    // já está aberto — pedido do usuário após teste ao vivo.
    onAbrir() {
      if (this.ativo) {
        this.$emit('fechar')
        return
      }
      this.$emit('abrir')
    },
    // `state.aberto ? state.parte.identidade : null` — direto do store, sem
    // `mapGetters` (ver comentário do cabeçalho). `null` quando o módulo não
    // está registrado (superfícies fora do painel de artefato).
    artefatoIdentidadeAtiva() {
      const store = this.$store
      if (!store) return null
      return store.getters[this.storeModule + '/artefatoIdentidadeAtiva'] || null
    },
  },
}
</script>

<template>
  <button
    type="button"
    class="artefato-preview-card d-flex align-center justify-space-between text-left ma-0"
    :class="classesDoCard"
    :aria-label="titulo"
    :aria-current="ariaCurrent"
    data-test="artefato-preview-card"
    @click="onAbrir"
    @mouseenter="isHover = true"
    @mouseleave="isHover = false"
  >
    <span
      class="artefato-preview-card__tile d-flex align-center justify-center flex-shrink-0"
      :class="classesDoTile"
      data-test="artefato-preview-card-tile"
    >
      <icon-svg
        class="navbar-main-icon"
        :class="{ 'is-detailed': isDetailedIcon, 'is-animating': isHover }"
        :name="icone"
        :size="tamanhoDoIcone"
        :color="corDoIcone"
      />
    </span>

    <span class="artefato-preview-card__texto az-gap-xsmall" :class="classesDoTexto">
      <span class="artefato-preview-card__titulo ia-texto-corpo text-truncate" :title="titulo">{{ titulo }}</span>
      <span
        v-if="subtitulo"
        class="artefato-preview-card__subtitulo ia-texto-auxiliar text-truncate"
        :title="subtitulo"
      >
        {{ subtitulo }}
      </span>
    </span>

    <!-- Modo mini: sem CTA/chip — o tempo (quando existe) e um chevron, como
         a linha de artefato do Claude.ai. -->
    <span
      v-if="mini"
      class="artefato-preview-card__direita d-inline-flex align-center az-gap-xsmall flex-shrink-0 ml-3"
      data-test="artefato-preview-card-direita"
    >
      <span v-if="tempo" class="artefato-preview-card__tempo ia-texto-auxiliar" data-test="artefato-preview-card-tempo">
        {{ tempo }}
      </span>
      <!-- Linha ATIVA: no hover o chevron dá lugar ao close-circle — o mesmo
           affordance "Fechar painel" do card do thread, trocado por CSS puro
           (`visibility`, como os dois chips do modo normal). -->
      <span class="artefato-preview-card__seta-wrap d-inline-flex">
        <icon-svg
          class="artefato-preview-card__seta"
          name="mdi-chevron-right"
          :size="TAMANHO_DO_CHEVRON"
          color="var(--neutral-400)"
        />
        <icon-svg
          v-if="ativo"
          class="artefato-preview-card__fechar-mini"
          name="close-circle"
          :size="TAMANHO_DO_CHEVRON"
          color="primary"
          data-test="artefato-preview-card-fechar-mini"
        />
      </span>
    </span>
    <span v-else-if="ativo" class="artefato-preview-card__chip-wrap flex-shrink-0 my-3 mx-4">
      <span
        class="artefato-preview-card__chip ia-texto-selo artefato-preview-card__chip--padrao align-center az-gap-xsmall flex-shrink-0"
        data-test="artefato-preview-card-chip"
      >
        <icon-svg name="check-circle" :size="14" color="primary" />
        No painel
      </span>
      <span
        class="artefato-preview-card__chip ia-texto-selo artefato-preview-card__chip--hover align-center az-gap-xsmall flex-shrink-0"
        data-test="artefato-preview-card-chip-hover"
      >
        <icon-svg name="close-circle" :size="14" color="primary" />
        Fechar painel
      </span>
    </span>
    <span
      v-else
      class="artefato-preview-card__cta ia-texto-corpo d-inline-flex align-center az-gap-xxsmall flex-shrink-0 my-3 mx-4"
      data-test="artefato-preview-card-cta"
    >
      {{ copy.ABRIR }}
      <icon-svg name="mdi-chevron-right" :size="16" color="primary" />
    </span>
  </button>
</template>

<style lang="sass" scoped>
// ★ O mapa de animações POR ÍCONE do menu lateral de módulos — o MESMO
// partial que `navegacao-v2/components/nav-item.vue` importa (é a cópia
// reutilizável do `<style scoped>` do `button-navbar.vue`, sem o ancestral
// `.button-navbar`). Ele assume `<icon-svg class="navbar-main-icon">` com os
// modificadores `.is-detailed`/`.is-animating` (aplicados no template acima)
// e os `@keyframes az-*` globais de `src/design/helpers/_effects.sass`.
// Ícone sem entrada no mapa não anima — igual à navbar. O bloco de
// `prefers-reduced-motion` também vem de lá.
@import '@modules/navegacao-v2/styles/_nav-icon-tells.sass'

// display/align/justify/text-align/margin/padding cobertos por utility
// classes Vuetify 2 (d-flex, align-center, justify-space-between, text-left,
// ma-0, pa-0, py-3, pl-4, ml-3, my-3, mx-4, d-inline-flex, flex-shrink-0,
// align-self-center) e o respiro por .az-gap-xsmall/.az-gap-xxsmall
// (src/design/aawz/az-spacing.sass) — todos no template. Só o TAMANHO do
// tile fica em Sass (40/32px não são utility) — ver `__tile`.
//
// O padding do CARD é zero (`pa-0`): o respiro interno vive nos blocos de
// texto (`py-3 pl-4`), da direita (`my-3 mx-4`) e no `ml-3` do tile — assim
// o chip de hover absoluto continua ancorado em `top/right: 0` da margem.
//
// border-radius: `--radius-lg` (12px, `src/design/_controls.sass`) — antes
// era 12px literal com a nota de que `design-system/tokens.json` não tinha
// o valor; o token de `_controls.sass` tem, e é global.
// overflow: hidden — mantido por segurança (nada deve vazar do raio); não
// afeta o `:focus-visible` (outline do próprio elemento não é recortado) e o
// chip de hover (absoluto) fica dentro da caixa.
// min-height: 60px — PISO (não teto): reserva o espaço mínimo mesmo quando o
// subtítulo está ausente, para todo card ter altura consistente numa lista
// vertical; com as 2 linhas (título+subtítulo) o card cresce além disso
// naturalmente — min-height nunca corta conteúdo.
//
// min-width: 264px — PISO em px, não só `max-width: 50%`. Causa raiz de um
// bug real (print do usuário): com o aside do painel de artefato aberto, a
// bolha do chat fica bem estreita e 50% dela vira ~150-200px — MENOS do que
// a soma dos elementos que NUNCA encolhem dentro do card (tile 48px + a
// margem dele 12px + padding do texto 16px + o bloco do CTA "Abrir artefato"
// + chevron com as próprias margens, ~195px, todos com `flex-shrink-0`). Sem
// piso, sobra ~0px
// pro texto do título truncar — ele já usa `text-truncate` corretamente
// (ellipsis por span, ver `.artefato-preview-card__titulo`/`__subtitulo`),
// mas truncando para 1 caractere o `justify-space-between` não tem folga
// nenhuma e cola o resto do título direto no texto do CTA — lê como
// "AAbrir artefato". 264px é a soma dos elementos fixos (~240px) + uma
// folga mínima para o título mostrar 2-3 caracteres + reticências antes de
// truncar de verdade. Quando o container pai for menor que isso, o card
// ULTRAPASSA os 50% (min-width vence max-width em conflito real de CSS) — é
// o comportamento aceito quando não há espaço pra ficar bonito em 50%.
// 60px é também a altura REAL na prática: título + subtítulo com `py-3`
// somam ~54px, abaixo do piso — a geometria do tile abaixo conta com isso.
$altura-minima-do-card: 60px
$borda-do-card: 1px

.artefato-preview-card
  width: 100%
  max-width: 50%
  min-width: 264px
  min-height: $altura-minima-do-card
  border: $borda-do-card solid var(--border-hairline)
  border-radius: var(--radius-lg)
  overflow: hidden
  background: var(--white)
  cursor: pointer
  font: inherit
  color: inherit
  outline: none
  transition: background-color 0.2s var(--ease-out, ease-out), border-color 0.2s var(--ease-out, ease-out)

  // Estado 2 (hover): borda primary + wash MUITO sutil sobre o branco —
  // opacidade bem menor que os 15% do tile (esse é o "quase imperceptível"
  // do mock), via color-mix (mesmo padrão de escopo-select.vue/
  // conversation-row.vue neste módulo, nunca hex/rgba cru).
  &:hover
    border-color: var(--primary)
    background: color-mix(in srgb, var(--primary) 6%, var(--white))

  &:focus-visible
    outline: 2px solid var(--primary)
    outline-offset: 2px

  // Estado 3 (ativo): borda `primary` (mesma espessura hairline do default,
  // 1px — pedido do usuário: 2px lia "grossa demais") e wash AINDA mais
  // sutil que o hover (3% < 6%) — precisa ser distinguível do hover simples
  // por ser persistente, não transitório; a distinção real vem da COR da
  // borda + do tile sólido + chip "No painel", o wash é só reforço.
  // `&:hover` aninhado garante que a especificidade do ativo vence mesmo com
  // o mouse em cima (mesma especificidade de duas classes; ordem no arquivo
  // desempata a favor do ativo, e o `:hover` aninhado sobe a especificidade
  // de vez).
  &.artefato-preview-card--ativo
    border-color: var(--primary)
    background: color-mix(in srgb, var(--primary) 3%, var(--white))

    &:hover
      background: color-mix(in srgb, var(--primary) 3%, var(--white))

// ── O tile: quadrado arredondado, centrado, à esquerda ─────────────────────
// Decisão do usuário (11/09/2026): centrado, padrão AAWZ — o tile "afundado"
// no rodapé, estilo Claude.ai (margem negativa cortada por `overflow`, raio só
// no topo, ancorado nas duas bordas), foi descartado depois de seis rodadas.
// 40×40 no thread, 32×32 no `--mini`; raio `--radius-md` nos 4 cantos (o mini
// usa `--radius-sm`, proporcional). O tint é o MESMO 15% de `primary` que o
// `IconWithBackground` usava — via color-mix, nunca rgba cru.
$lado-do-tile: 40px
$lado-do-tile-mini: 32px

.artefato-preview-card__tile
  width: $lado-do-tile
  height: $lado-do-tile
  box-sizing: border-box
  border-radius: var(--radius-md)
  background: color-mix(in srgb, var(--primary) 15%, var(--white))
  transition: background-color 0.2s var(--ease-out, ease-out)

// Estado 3: tile sólido `primary` (o ícone vira branco pelo `corDoIcone`).
.artefato-preview-card--ativo .artefato-preview-card__tile
  background: var(--primary)

.artefato-preview-card__texto
  display: inline-flex
  flex-direction: column
  // `flex: 1 1 auto` + `min-width: 0` é o que permite este bloco encolher
  // abaixo do próprio min-content dentro do flex row do botão — sem isso o
  // texto nunca cede espaço pro bloco da direita (chip/CTA), o
  // `justify-space-between` não tem efeito e o título não trunca.
  flex: 1 1 auto
  min-width: 0

// Estado 3: pílula "No painel" — fundo bem clarinho de primary, texto
// primary, com o ícone de check (gap entre os dois vem de `.az-gap-xsmall`
// no template — não é ação de tabela, a regra de "sem gap entre ícones de
// tabela" não se aplica a este chip).
//
// O card ATIVO tem DOIS chips empilhados no mesmo lugar (`--padrao`/
// `--hover`), trocados por CSS puro no `:hover` do CARD — sem JS de
// mouseenter/leave (o `isHover` do script existe SÓ para a classe
// `is-animating` do ícone, e não participa desta troca). O padrão fica
// visível por padrão e some no hover; o de hover nasce escondido e só
// aparece com o mouse em cima do card. O wrapper (`__chip-wrap`) é
// `position: relative` para o chip de hover (absoluto) ancorar exatamente
// sobre o padrão, sem o card mudar de tamanho na troca. O respiro do bloco
// da direita é MARGEM (`my-3 mx-4`), não padding, de propósito: margem fica
// FORA da caixa de referência do `absolute`, então `top: 0`/`right: 0` do
// `--hover` cai exatamente sobre o `--padrao` sem número mágico.
//
// ★ BUG achado e corrigido (relatado como "Fechar painel fica visível fora
// do hover"): os dois `<span>` chegaram a levar a utility `d-inline-flex` do
// Vuetify no template. Essa classe é `display: inline-flex !important`
// (node_modules/vuetify/dist/vuetify.css), e `!important` sempre vence uma
// declaração sem `!important` — não importa a especificidade nem a ordem no
// cascade. Resultado: o `display: none` deste arquivo NUNCA tinha efeito
// nenhum sobre `__chip--hover`, que ficava com `display: inline-flex`
// SEMPRE; como ele é `position: absolute` por cima do chip `--padrao`, "Fechar
// painel" ficava desenhado por cima o tempo todo, hover ou não — não é
// comportamento nativo de `:hover` "grudando" depois do clique, era a troca
// nunca acontecendo de fato. Fix: os dois chips não levam mais `d-inline-flex`
// no template — o `display: inline-flex` default vem daqui (regra abaixo),
// sem `!important`, o que deixa o toggle condicional funcionar de verdade.
//
// ★ 2º BUG achado e corrigido (relatado ao vivo: "no hover, o 'Fechar
// painel' tá deslocando pra baixo"). Causa raiz: o `--padrao` era o ÚNICO
// filho EM FLUXO do `__chip-wrap` — era ele quem dava tamanho (altura, ~22px)
// ao wrapper; o `--hover` é `position: absolute` e nunca contava pra esse
// tamanho. Ao entrar no `:hover`, a regra virava `--padrao` pra
// `display: none`, o que o TIRA do fluxo por completo — sem nenhum filho em
// fluxo, o `__chip-wrap` colapsava pra ALTURA ZERO. Como `__chip-wrap` é um
// item de flex do card (`.artefato-preview-card` é `align-items: center`),
// um item que encolhe de ~22px pra 0px tem o TOPO da própria caixa
// deslocado pra baixo em ~metade da altura anterior (o centro vertical do
// item continua o mesmo; o topo é que se aproxima dele). O `--hover`,
// ancorado em `top: 0` relativo a essa caixa, descia junto — exatamente o
// deslocamento pra baixo relatado. Fix: o toggle deixou de usar `display`
// (que tira do fluxo) e passou a usar `visibility` (que ESCONDE mas
// preserva o espaço no fluxo). O `--padrao` nunca mais sai do fluxo — o
// `__chip-wrap` fica com o MESMO tamanho em qualquer estado, hover ou não,
// e o `--hover` (sempre `position: absolute`, ancorado nesse tamanho
// estável) nunca mais se desloca.
.artefato-preview-card__chip-wrap
  position: relative

.artefato-preview-card__chip
  color: var(--primary)
  display: inline-flex
  padding: 4px 10px
  border-radius: 999px
  background: color-mix(in srgb, var(--primary) 12%, var(--white))
  white-space: nowrap

.artefato-preview-card__chip--hover
  visibility: hidden
  position: absolute
  top: 0
  right: 0

.artefato-preview-card--ativo:hover .artefato-preview-card__chip--padrao
  visibility: hidden

.artefato-preview-card--ativo:hover .artefato-preview-card__chip--hover
  visibility: visible

// Estados 1/2/4: "Abrir artefato" + chevron, em primary (gap via
// `.az-gap-xxsmall` no template).
.artefato-preview-card__cta
  color: var(--primary)
  white-space: nowrap

// ── Modo mini: a linha do índice "Artefatos desta conversa" ───────────────
// Sem borda, sem piso/teto de largura (100% da linha), fundo do popover —
// hover/ativo no MESMO par quente do `chat-indice-mensagens__item`, porque a
// linha vive naquele popover, não no thread. Vem por ÚLTIMO para vencer as
// regras do card e do tile acima na mesma especificidade.
.artefato-preview-card--mini
  max-width: none
  min-width: 0
  min-height: 0
  border: 0
  border-radius: var(--radius-sm)
  overflow: visible
  background: transparent

  &:hover
    background: var(--sidebar-warm-hover)

  &.artefato-preview-card--ativo
    background: var(--sidebar-warm-bg)

    &:hover
      background: var(--sidebar-warm-bg)

  // A MESMA regra do tile, só menor.
  .artefato-preview-card__tile
    width: $lado-do-tile-mini
    height: $lado-do-tile-mini
    border-radius: var(--radius-sm)

.artefato-preview-card__tempo
  color: var(--neutral-700)
  white-space: nowrap

// Chevron × close-circle na linha ativa: mesma troca por `visibility` dos
// dois chips do modo normal (nunca `display`, para o wrapper não mudar de
// tamanho no hover).
.artefato-preview-card__seta-wrap
  position: relative

.artefato-preview-card__fechar-mini
  visibility: hidden
  position: absolute
  top: 0
  right: 0

.artefato-preview-card--mini.artefato-preview-card--ativo:hover
  .artefato-preview-card__seta
    visibility: hidden

  .artefato-preview-card__fechar-mini
    visibility: visible

// A cor do papel (leva 2 da escala: a classe `ia-texto-*` não carrega cor).
.artefato-preview-card__titulo
  color: var(--neutral-900)

// A cor do papel (leva 2 da escala: a classe `ia-texto-*` não carrega cor).
.artefato-preview-card__subtitulo
  color: var(--neutral-700)

</style>
